import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  ShieldCheck,
  CheckCircle2,
  AlertCircle,
  FileText,
  MapPin,
  Download,
  Building,
} from 'lucide-react';
import { episService } from '../../services/epis.service';
import { EpiCameraCapture } from './components/EpiCameraCapture';
import { EpiSignatureCanvas } from './components/EpiSignatureCanvas';
import type { EpiEntregaContract } from '@sstexames/shared-types';

export const EpiAceitePublico: React.FC = () => {
  const { token } = useParams<{ token: string }>();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [entrega, setEntrega] = useState<EpiEntregaContract | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Form states
  const [fotoColaboradorBase64, setFotoColaboradorBase64] = useState<string>('');
  const [assinaturaBase64, setAssinaturaBase64] = useState<string>('');
  const [telefone, setTelefone] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [concordoTermos, setConcordoTermos] = useState<boolean>(false);
  const [geoCoords, setGeoCoords] = useState<{ lat?: number; lng?: number }>({});
  const [sucesso, setSucesso] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  // Load entrega data by public token
  useEffect(() => {
    if (!token) {
      setErrorMessage('Token de entrega inválido ou ausente.');
      setLoading(false);
      return;
    }

    const fetchEntrega = async () => {
      try {
        setLoading(true);
        const data = await episService.getEntregaPublica(token);
        setEntrega(data);
        if (data.status === 'entregue') {
          setSucesso(true);
        }
      } catch (err: unknown) {
        console.error('Erro ao carregar entrega por token:', err);
        const errorMsg =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          'Não foi possível localizar esta ficha de entrega. Verifique se o link está correto ou expirou.';
        setErrorMessage(errorMsg);
      } finally {
        setLoading(false);
      }
    };

    fetchEntrega();

    // Capture geolocation if permitted
    if (typeof navigator !== 'undefined' && 'geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setGeoCoords({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
          });
        },
        (err) => {
          console.warn('Geolocalização não autorizada:', err.message);
        },
        { timeout: 8000 },
      );
    }
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    if (!token) return;

    if (!concordoTermos) {
      setErrorMessage('Você deve concordar com os termos de responsabilidade para assinar.');
      return;
    }
    if (!fotoColaboradorBase64) {
      setErrorMessage('A sua foto de reconhecimento facial é obrigatória para validação da entrega.');
      return;
    }
    if (!assinaturaBase64) {
      setErrorMessage('Por favor, desenhe a sua assinatura no campo indicado.');
      return;
    }

    try {
      setSubmitting(true);
      await episService.assinarEntregaPublica(token, {
        token_assinatura: token,
        foto_colaborador_base64: fotoColaboradorBase64,
        assinatura_base64: assinaturaBase64,
        geo_latitude: geoCoords.lat,
        geo_longitude: geoCoords.lng,
        colaborador_dados_confirmados: {
          telefone: telefone || undefined,
          email: email || undefined,
        },
      });

      setSucesso(true);
    } catch (err: unknown) {
      console.error('Erro ao assinar entrega:', err);
      const errorMsg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Erro ao assinar ficha de EPI.';
      setErrorMessage(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!token) return;
    try {
      setDownloadingPdf(true);
      const blob = await episService.downloadPdfPublico(token);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Ficha_EPI_${token.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Erro ao baixar PDF:', err);
      alert('Não foi possível gerar o PDF no momento. Tente novamente.');
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center space-y-3">
          <div className="w-10 h-10 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-sm font-medium text-slate-600">Carregando Ficha de EPI...</p>
        </div>
      </div>
    );
  }

  if (errorMessage && !entrega) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-6 text-center border border-slate-200">
          <div className="w-12 h-12 rounded-full bg-rose-50 text-rose-600 flex items-center justify-center mx-auto mb-3 border border-rose-100">
            <AlertCircle className="w-6 h-6" />
          </div>
          <h3 className="text-base font-bold text-slate-900 mb-1">Acesso Indisponível</h3>
          <p className="text-xs text-slate-600 mb-4">{errorMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 to-slate-200 py-6 px-4 sm:px-6">
      <div className="max-w-xl mx-auto">
        {/* Brand Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-primary-600 text-white shadow-md mb-2">
            <ShieldCheck className="w-7 h-7" />
          </div>
          <h1 className="text-xl font-extrabold text-slate-900 tracking-tight">Ficha Digital de EPI (NR-06)</h1>
          <p className="text-xs text-slate-500">Conferência, Biometria Facial e Assinatura Eletrônica</p>
        </div>

        {sucesso ? (
          /* Card de Conclusão */
          <div className="bg-white rounded-3xl shadow-xl border border-slate-200 p-8 text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto border border-emerald-100 shadow-inner">
              <CheckCircle2 className="w-8 h-8" />
            </div>

            <h2 className="text-lg font-bold text-slate-900">Entrega Concluída com Sucesso!</h2>
            <p className="text-xs text-slate-600 leading-relaxed">
              Obrigado, <strong>{entrega?.colaborador_nome}</strong>! Sua assinatura e biometria facial foram registradas com sucesso. A integridade desta ficha é garantida digitalmente por carimbo de tempo e hash criptográfico SHA-256 conforme as diretrizes da NR-06.
            </p>

            <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 text-left text-xs text-slate-600 space-y-1">
              <div>
                Empresa: <strong>{entrega?.empresa_nome}</strong>
              </div>
              <div>
                Colaborador: <strong>{entrega?.colaborador_nome}</strong> (CPF: {entrega?.colaborador_cpf})
              </div>
              <div>
                Data da Assinatura: <strong>{new Date().toLocaleDateString('pt-BR')}</strong>
              </div>
              {entrega?.hash_integridade && (
                <div className="pt-1 text-[10px] text-slate-400 font-mono break-all">
                  Hash: {entrega.hash_integridade}
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
              className="w-full py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-xl text-xs font-bold transition-all shadow-md flex items-center justify-center gap-2"
            >
              <Download className="w-4 h-4" />
              {downloadingPdf ? 'Gerando PDF Oficial...' : 'Baixar Comprovante em PDF'}
            </button>
          </div>
        ) : (
          /* Formulário de Conferência e Assinatura */
          <div className="bg-white rounded-3xl shadow-xl border border-slate-200 overflow-hidden">
            {/* Header da Empresa & Colaborador */}
            <div className="p-5 bg-slate-50 border-b border-slate-200 space-y-3">
              <div className="flex items-center gap-2.5">
                <Building className="w-4 h-4 text-primary-600" />
                <span className="text-xs font-bold text-slate-900">{entrega?.empresa_nome}</span>
              </div>

              <div className="p-3 bg-white rounded-xl border border-slate-200 text-xs space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Colaborador:</span>
                  <span className="font-bold text-slate-800">{entrega?.colaborador_nome}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">CPF:</span>
                  <span className="font-mono text-slate-700">{entrega?.colaborador_cpf}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Cargo / Função:</span>
                  <span className="font-medium text-slate-800">{entrega?.cargo_nome}</span>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-5 space-y-6">
              {errorMessage && (
                <div className="bg-rose-50 border border-rose-200 text-rose-800 px-4 py-2.5 rounded-xl text-xs flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{errorMessage}</span>
                </div>
              )}

              {/* Lista de Equipamentos Recebidos */}
              <div>
                <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <FileText className="w-4 h-4 text-primary-600" /> Equipamentos Entregues para Você
                </h3>

                <div className="space-y-2">
                  {entrega?.itens.map((item, idx) => (
                    <div
                      key={idx}
                      className="p-3 rounded-xl border border-slate-200 bg-slate-50/50 flex items-center justify-between"
                    >
                      <div>
                        <span className="text-xs font-bold text-slate-900 block">{item.descricao_epi}</span>
                        <span className="text-[11px] text-slate-500 block">
                          Fabricante: {item.fabricante || 'Standard'} | CA: {item.ca_numero || 'Regulamentado'}
                        </span>
                        {item.tamanho && (
                          <span className="text-[10px] text-slate-600">Tamanho: {item.tamanho}</span>
                        )}
                      </div>
                      <span className="text-xs font-bold bg-primary-50 text-primary-700 border border-primary-100 rounded-lg px-2.5 py-1">
                        {item.quantidade} un.
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Confirmação de Contato Opcional */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] text-slate-500 block mb-1">WhatsApp / Celular (Opcional)</label>
                  <input
                    type="tel"
                    placeholder="(00) 00000-0000"
                    value={telefone}
                    onChange={(e) => setTelefone(e.target.value)}
                    className="w-full px-3 py-2 text-xs border border-slate-300 rounded-xl"
                  />
                </div>
                <div>
                  <label className="text-[11px] text-slate-500 block mb-1">E-mail (Opcional)</label>
                  <input
                    type="email"
                    placeholder="seu.email@exemplo.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-3 py-2 text-xs border border-slate-300 rounded-xl"
                  />
                </div>
              </div>

              {/* Termo de Responsabilidade NR-06 */}
              <div className="p-4 bg-amber-50/60 border border-amber-200 rounded-2xl text-[11px] text-amber-900 leading-relaxed space-y-2">
                <span className="font-bold block uppercase tracking-wider text-[10px] text-amber-800">
                  Termo de Responsabilidade e Guarda (NR-06)
                </span>
                <p>
                  Declaro que recebi gratuitamente da empresa os Equipamentos de Proteção Individual (EPIs) relacionados acima, em perfeito estado de conservação e funcionamento, novos ou devidamente higienizados.
                </p>
                <p>
                  Comprometo-me a utilizá-los estritamente para as finalidades a que se destinam durante toda a minha jornada de trabalho, responsabilizando-me por sua guarda, zelo e conservação, e comunicando imediatamente ao empregador qualquer alteração que os torne impróprios para uso.
                </p>
              </div>

              <label className="flex items-start gap-2.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={concordoTermos}
                  onChange={(e) => setConcordoTermos(e.target.checked)}
                  className="mt-0.5 w-4 h-4 text-primary-600 rounded border-slate-300 focus:ring-primary-500"
                />
                <span className="text-xs text-slate-700 font-medium">
                  Li, compreendi e concordo integralmente com os termos e com a conferência dos itens recebidos.
                </span>
              </label>

              {/* Biometria Facial */}
              <div className="border-t border-slate-100 pt-4">
                <EpiCameraCapture
                  label="1. Sua Foto Facial (Biometria de Reconhecimento) *"
                  onPhotoCaptured={(base64) => setFotoColaboradorBase64(base64)}
                  initialPhotoUrl={fotoColaboradorBase64 || null}
                  facingMode="user"
                />
              </div>

              {/* Assinatura no Canvas */}
              <div className="border-t border-slate-100 pt-4">
                <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider block mb-2">
                  2. Sua Assinatura / Rubrica com o Dedo ou Caneta *
                </span>
                <EpiSignatureCanvas
                  onSave={(base64) => setAssinaturaBase64(base64)}
                  onClear={() => setAssinaturaBase64('')}
                />
              </div>

              {/* Geolocalização Indicator */}
              {geoCoords.lat && (
                <div className="flex items-center gap-1.5 text-[10px] text-slate-400">
                  <MapPin className="w-3.5 h-3.5 text-emerald-500" />
                  <span>
                    Localização capturada: {geoCoords.lat.toFixed(4)}, {geoCoords.lng?.toFixed(4)}
                  </span>
                </div>
              )}

              {/* Botão de Envio */}
              <button
                type="submit"
                disabled={submitting || !concordoTermos}
                className="w-full py-3.5 bg-primary-600 hover:bg-primary-700 text-white rounded-2xl text-xs font-extrabold tracking-wide uppercase shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {submitting ? 'Gravando Biometria e Assinatura...' : 'Confirmar e Assinar Entrega de EPI'}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default EpiAceitePublico;
